import { useSessionStore } from '../stores/sessionStore';

/**
 * The one place the web site renews its Keycloak sign-in.
 *
 * Two callers share it: the axios 401 interceptor (api/client.ts) and the
 * proactive renewal in useSessionLifetime, which renews shortly before the
 * access token runs out while the user is working. Sharing one function means
 * they can never run two renewals at once: a second caller joins the renewal
 * already in flight and gets the same answer.
 *
 * Guards (automaticSilentRenew stays off; see authConfig.ts for why):
 * - once the refresh token is known to be dead nothing renews again until the
 *   page reloads or the user signs in again (no storm against Keycloak);
 * - a proactive renewal that fails for a passing reason (network, timeout)
 *   backs off 30 s, 60 s, 120 s and then stops trying for that token. The 401
 *   interceptor still gets one attempt when a request actually needs it.
 */

export type RenewReason = 'request' | 'proactive' | 'user';

export interface SessionAuthActions {
  /** Renew the tokens. Resolving to `null` means it failed (react-oidc-context's wrapper does that). */
  signinSilent: () => Promise<unknown>;
  /** Full sign-in through the Keycloak login page. Leaves the page. */
  signinRedirect: () => Promise<void>;
}

/** Back-off after a proactive renewal fails for a passing reason, in ms. */
export const PROACTIVE_BACKOFF_MS = [30_000, 60_000, 120_000];
/** Proactive attempts per access token before leaving it to the 401 interceptor. */
export const MAX_PROACTIVE_FAILURES = PROACTIVE_BACKOFF_MS.length;

let actions: SessionAuthActions | null = null;
let inFlight: Promise<boolean> | null = null;
let dead = false;
let proactiveFailures = 0;
let proactiveNotBefore = 0;
let lastResult: 'none' | 'renewed' | 'transient' | 'dead' = 'none';

export function setSessionAuthActions(next: SessionAuthActions | null) {
  actions = next;
}

export function getSessionAuthActions(): SessionAuthActions | null {
  return actions;
}

export function isSessionDead(): boolean {
  return dead;
}

/** The outcome of the most recent renewal attempt (for the banner's "couldn't reach" text). */
export function lastRenewalResult() {
  return lastResult;
}

/** True when a proactive renewal may run now (not dead, not in flight, not backing off). */
export function canRenewProactively(now = Date.now()): boolean {
  return !dead && !inFlight && proactiveFailures < MAX_PROACTIVE_FAILURES && now >= proactiveNotBefore;
}

export function isRenewing(): boolean {
  return inFlight !== null;
}

/**
 * The refresh token can no longer be used. Stops every further renewal and
 * tells the banner, which asks the user to save and sign in again. The
 * session-expired dialog follows when a request fails or the access token
 * runs out.
 */
export function markSessionDead() {
  dead = true;
  lastResult = 'dead';
  useSessionStore.getState().setRenewalFailed();
}

/** Keycloak said no (invalid_grant, login_required, ...): the refresh token is finished. */
function isDefinitiveRejection(err: unknown): boolean {
  const e = err as { name?: unknown; error?: unknown } | null;
  return !!e && (e.name === 'ErrorResponse' || typeof e.error === 'string');
}

/**
 * Renew the sign-in once. Resolves true when new tokens were stored.
 *
 * - 'request': a request came back 401. Any failure marks the session dead
 *   (the behaviour the interceptor has always had, so a bad refresh token can
 *   never storm).
 * - 'proactive': ahead of expiry while the user is active. Respects back-off;
 *   only a definite rejection from Keycloak marks the session dead.
 * - 'user': the notice's "I'm still here". Ignores back-off; only a definite
 *   rejection marks the session dead.
 */
export function renewSession(reason: RenewReason): Promise<boolean> {
  if (dead) return Promise.resolve(false);
  if (inFlight) return inFlight;
  if (reason === 'proactive' && !canRenewProactively()) return Promise.resolve(false);

  if (!actions) {
    if (reason === 'request') markSessionDead();
    return Promise.resolve(false);
  }

  const renew = actions.signinSilent;
  inFlight = (async () => {
    let failure: unknown = null;
    try {
      const result = await renew();
      if (result === null) failure = new Error('Sign-in renewal returned no user');
    } catch (err) {
      failure = err ?? new Error('Sign-in renewal failed');
    }

    if (!failure) {
      proactiveFailures = 0;
      proactiveNotBefore = 0;
      lastResult = 'renewed';
      return true;
    }

    if (reason === 'request' || isDefinitiveRejection(failure)) {
      markSessionDead();
      return false;
    }

    lastResult = 'transient';
    if (reason === 'proactive') {
      const wait = PROACTIVE_BACKOFF_MS[Math.min(proactiveFailures, PROACTIVE_BACKOFF_MS.length - 1)];
      proactiveFailures += 1;
      proactiveNotBefore = Date.now() + wait;
    }
    return false;
  })().finally(() => {
    inFlight = null;
  });
  return inFlight;
}

/** A new access token arrived: proactive renewal may try again for it. */
export function resetProactiveBackoff() {
  proactiveFailures = 0;
  proactiveNotBefore = 0;
}

/** After the user signs in again (or in tests): forget everything. */
export function resetSessionRenewal() {
  inFlight = null;
  dead = false;
  proactiveFailures = 0;
  proactiveNotBefore = 0;
  lastResult = 'none';
}
